import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Mail, Building2, Users as UsersIcon } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog as RealAlertDialog,
  AlertDialogAction as RealAlertAction, AlertDialogCancel as RealAlertCancel,
  AlertDialogContent as RealAlertContent, AlertDialogDescription as RealAlertDesc,
  AlertDialogFooter as RealAlertFooter, AlertDialogHeader as RealAlertHeader,
  AlertDialogTitle as RealAlertTitle, AlertDialogTrigger as RealAlertTrigger,
} from "../components/ui/alert-dialog";

const MODULES = ["dashboard", "requests", "approvals", "purchase_orders", "vendors", "inventory", "analytics", "settings"];

export default function Settings() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const [tab, setTab] = useState("org");

  const [org, setOrg] = useState({ name: "", logo_url: "", currency: "INR", timezone: "Asia/Kolkata" });
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [newCc, setNewCc] = useState("");
  const [groupOpen, setGroupOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [gForm, setGForm] = useState({ name: "", privileges: [], member_ids: [] });

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  const load = async () => {
    const [o, g, u, c] = await Promise.all([
      api.get("/org"),
      api.get("/groups").catch(() => ({ data: [] })),
      api.get("/auth/users").catch(() => ({ data: [] })),
      api.get("/cc-emails"),
    ]);
    if (o.data?.name) setOrg(o.data);
    setGroups(g.data || []); setUsers(u.data || []); setCcs(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveOrg = async () => {
    try {
      await api.patch("/org", org);
      toast.success("Organization updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addCc = async () => {
    if (!newCc.trim()) return;
    try {
      await api.post("/cc-emails", { email: newCc });
      setNewCc(""); load();
      toast.success("Added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCc = async (id) => {
    try { await api.delete(`/cc-emails/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error("Failed"); }
  };

  const openCreateGroup = () => { setEditGroup(null); setGForm({ name: "", privileges: [], member_ids: [] }); setGroupOpen(true); };
  const openEditGroup = (g) => { setEditGroup(g); setGForm({ name: g.name, privileges: g.privileges || [], member_ids: g.member_ids || [] }); setGroupOpen(true); };

  const saveGroup = async () => {
    if (!gForm.name.trim()) return toast.error("Name required");
    try {
      if (editGroup) await api.patch(`/groups/${editGroup.id}`, gForm);
      else await api.post("/groups", gForm);
      toast.success("Saved"); setGroupOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteGroup = async (g) => {
    try { await api.delete(`/groups/${g.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const togglePrivilege = (m) => {
    setGForm({ ...gForm, privileges: gForm.privileges.includes(m) ? gForm.privileges.filter((x) => x !== m) : [...gForm.privileges, m] });
  };

  const toggleMember = (id) => {
    setGForm({ ...gForm, member_ids: gForm.member_ids.includes(id) ? gForm.member_ids.filter((x) => x !== id) : [...gForm.member_ids, id] });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="settings-title">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Workspace, groups, notifications.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="k-glass">
          <TabsTrigger value="org" data-testid="tab-org"><Building2 size={14} className="mr-1" /> Organization</TabsTrigger>
          {isAdmin && <TabsTrigger value="groups" data-testid="tab-groups"><UsersIcon size={14} className="mr-1" /> Groups</TabsTrigger>}
          <TabsTrigger value="cc" data-testid="tab-cc"><Mail size={14} className="mr-1" /> CC Emails</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Appearance</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "org" && (
        <div className="k-glass rounded-2xl p-6 space-y-4 max-w-xl">
          <div>
            <Label>Organization name</Label>
            <Input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} disabled={!isAdmin} data-testid="org-name" />
          </div>
          <div>
            <Label>Logo URL</Label>
            <Input value={org.logo_url || ""} onChange={(e) => setOrg({ ...org, logo_url: e.target.value })} disabled={!isAdmin} data-testid="org-logo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Currency</Label>
              <Select value={org.currency || "INR"} onValueChange={(v) => setOrg({ ...org, currency: v })} disabled={!isAdmin}>
                <SelectTrigger data-testid="org-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["INR", "USD", "EUR", "GBP", "AED"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Timezone</Label>
              <Select value={org.timezone || "Asia/Kolkata"} onValueChange={(v) => setOrg({ ...org, timezone: v })} disabled={!isAdmin}>
                <SelectTrigger data-testid="org-tz"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Asia/Kolkata", "UTC", "America/New_York", "Europe/London", "Asia/Dubai"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={saveOrg} className="bg-[#534AB7] hover:bg-[#463E9F]" data-testid="org-save">
              Save organization
            </Button>
          )}
        </div>
      )}

      {tab === "groups" && isAdmin && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={openCreateGroup} className="bg-[#534AB7] hover:bg-[#463E9F] gap-2" data-testid="add-group">
              <Plus size={14} /> New group
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {groups.length === 0 && <div className="k-glass rounded-2xl p-6 text-sm text-muted-foreground sm:col-span-3 text-center">No groups yet</div>}
            {groups.map((g) => (
              <div key={g.id} className="k-glass rounded-2xl p-4" data-testid={`group-${g.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{g.member_count} members</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditGroup(g)}>Edit</Button>
                    <RealAlertDialog>
                      <RealAlertTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive"><Trash2 size={14} /></Button>
                      </RealAlertTrigger>
                      <RealAlertContent>
                        <RealAlertHeader>
                          <RealAlertTitle>Delete group?</RealAlertTitle>
                          <RealAlertDesc>This cannot be undone.</RealAlertDesc>
                        </RealAlertHeader>
                        <RealAlertFooter>
                          <RealAlertCancel>Cancel</RealAlertCancel>
                          <RealAlertAction onClick={() => deleteGroup(g)} className="bg-red-600 hover:bg-red-700">Delete</RealAlertAction>
                        </RealAlertFooter>
                      </RealAlertContent>
                    </RealAlertDialog>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(g.privileges || []).map((p) => (
                    <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#534AB7]/10 text-[#534AB7]">{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editGroup ? "Edit group" : "New group"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Group name</Label>
                  <Input value={gForm.name} onChange={(e) => setGForm({ ...gForm, name: e.target.value })} data-testid="group-name" />
                </div>
                <div>
                  <Label className="mb-2 block">Module privileges</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {MODULES.map((m) => (
                      <label key={m} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-border hover:bg-foreground/5 cursor-pointer">
                        <Checkbox checked={gForm.privileges.includes(m)} onCheckedChange={() => togglePrivilege(m)} data-testid={`priv-${m}`} />
                        <span className="capitalize">{m.replace("_", " ")}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Members</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {users.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-border hover:bg-foreground/5 cursor-pointer">
                        <Checkbox checked={gForm.member_ids.includes(u.id)} onCheckedChange={() => toggleMember(u.id)} data-testid={`member-${u.id}`} />
                        <div className="min-w-0">
                          <div className="truncate">{u.name}</div>
                          <div className="text-[10px] text-muted-foreground capitalize">{u.role}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setGroupOpen(false)}>Cancel</Button>
                <Button onClick={saveGroup} className="bg-[#534AB7] hover:bg-[#463E9F]" data-testid="group-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === "cc" && (
        <div className="k-glass rounded-2xl p-6 space-y-4 max-w-xl">
          <h3 className="font-semibold">PO notification CC emails</h3>
          <div className="flex gap-2">
            <Input value={newCc} onChange={(e) => setNewCc(e.target.value)} placeholder="name@company.com" data-testid="cc-input" />
            <Button onClick={addCc} className="bg-[#534AB7] hover:bg-[#463E9F]" data-testid="cc-add">Add</Button>
          </div>
          <div className="space-y-2">
            {ccs.length === 0 && <p className="text-sm text-muted-foreground">No CC emails yet.</p>}
            {ccs.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-2 rounded-lg border border-border" data-testid={`cc-${c.id}`}>
                <span className="text-sm">{c.email}</span>
                {(isAdmin || isManager) && (
                  <button onClick={() => removeCc(c.id)} className="p-1 text-muted-foreground hover:text-destructive">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "appearance" && (
        <div className="k-glass rounded-2xl p-6 space-y-4 max-w-xl">
          <h3 className="font-semibold">Theme</h3>
          <p className="text-sm text-muted-foreground">Choose light or dark mode for your workspace.</p>
          <div className="flex gap-3">
            <button
              onClick={theme === "dark" ? toggle : undefined}
              className={`flex-1 p-4 rounded-xl border ${theme === "light" ? "border-[#534AB7] bg-[#534AB7]/10" : "border-border"}`}
              data-testid="theme-light"
            >
              <div className="w-full h-20 rounded-lg bg-gradient-to-br from-[#F0EFF8] to-white mb-2" />
              <div className="text-sm font-medium">Light</div>
            </button>
            <button
              onClick={theme === "light" ? toggle : undefined}
              className={`flex-1 p-4 rounded-xl border ${theme === "dark" ? "border-[#534AB7] bg-[#534AB7]/10" : "border-border"}`}
              data-testid="theme-dark"
            >
              <div className="w-full h-20 rounded-lg bg-gradient-to-br from-[#1A1830] to-[#2A2650] mb-2" />
              <div className="text-sm font-medium">Dark</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
